/**
 * Constants, types, pure helpers, and data-fetching utilities extracted from
 * MappingStep.tsx to keep the component file focused on rendering logic.
 */

import type {
  NewPropertySuggestion,
  StageValueSuggestion,
} from '@/lib/csv-import/api';
import { fetchWithDefaults } from '@/lib/http/client';

// ============================================================================
// Types
// ============================================================================

export interface PropertyTypeSchema {
  property_type_name: string;
  label: string;
  field_type_name: string;
  description: string;
  is_required: boolean;
  is_unique_value: boolean;
  is_multi?: boolean;
  value_write_access: 'all' | 'internal' | 'none';
  value_read_access: 'all' | 'internal';
}

export interface FileColumnInfo {
  fileId: string | number;
  filename: string;
  entityTypeName: string;
  columns: string[];
}

export type ActiveView =
  | { kind: 'file'; fileId: string | number }
  | { kind: 'associations' }
  | { kind: 'newProperties' };

export interface StageSuggestionState {
  isLoading: boolean;
  error: string | null;
  suggestion: StageValueSuggestion | null;
}

// ============================================================================
// Constants
// ============================================================================

export const STAGE_LABELS: Record<string, string> = {
  ahoy_deal: 'Deal Pipeline Stage',
  ahoy_contact: 'Contact Lifecycle Stage',
  ahoy_task: 'Status',
  ahoy_note: 'Status',
};

/**
 * Source entity type name -> stage entity type name, for the
 * `target_entity_type_name` on a stage-typed `crm_entity_association_mappings`
 * entry (see `CrmEntityAssociationMapping` in `@/lib/csv-import/api`).
 */
export const STAGE_ENTITY_TYPE_BY_SOURCE: Record<string, string> = {
  ahoy_deal: 'ahoy_deal_pipeline_stage',
  ahoy_contact: 'ahoy_contact_lifecycle_stage',
  ahoy_task: 'ahoy_task_pipeline_stage',
};

/**
 * Lookup for {@link STAGE_LABELS}: `undefined` for `ahoy_note`, since the
 * backend rejects it — otherwise defers to `STAGE_LABELS`.
 */
export function getStageLabelForSource(
  entityTypeName: string
): string | undefined {
  if (entityTypeName === 'ahoy_note') {
    return undefined;
  }
  return STAGE_LABELS[entityTypeName];
}

/**
 * Lookup for {@link STAGE_ENTITY_TYPE_BY_SOURCE} — same gating as
 * {@link getStageLabelForSource}.
 */
export function getStageEntityTypeForSource(
  entityTypeName: string
): string | undefined {
  if (entityTypeName === 'ahoy_note') {
    return undefined;
  }
  return STAGE_ENTITY_TYPE_BY_SOURCE[entityTypeName];
}

/**
 * System timestamp properties that MAY be mapped as import targets even though
 * they are not user-writable, so migrating customers keep their historical
 * timestamps (HubSpot "Create Date" / "Last Activity Date"). The backend
 * executor applies them via special mechanisms (backdated create-event
 * occurred_at / an internal write) rather than as regular property values.
 * ahoy_updated_at is deliberately excluded — it is engine-derived and an
 * imported value cannot stick.
 * Backend mirror: apps/csv_imports/constants.py
 * (IMPORTABLE_SYSTEM_TIMESTAMP_PROPERTIES).
 */
export const IMPORTABLE_SYSTEM_TIMESTAMP_PROPERTIES: ReadonlySet<string> =
  new Set(['ahoy_created_at', 'ahoy_last_activity_at']);

/**
 * Whether an import may map a CSV column onto this property: user-writable
 * properties plus the sanctioned system timestamps above. Mirrors the backend
 * suggester's `_is_importable_property` — the analyzer rejects anything else
 * at review time, so offering it in the dropdown would only manufacture
 * failures.
 */
export function isImportableProperty(p: PropertyTypeSchema): boolean {
  if (p.value_read_access === 'internal') {
    return false;
  }
  return (
    p.value_write_access === 'all' ||
    IMPORTABLE_SYSTEM_TIMESTAMP_PROPERTIES.has(p.property_type_name)
  );
}

/** Sentinel value used to represent the stage option in the select dropdown */
export const STAGE_SENTINEL = '__stage__';

/** Sentinel value used to represent the "Link to record" option in the select
 * dropdown. A column in this mode maps to a CrmEntityAssociationMapping rather
 * than a property — its target type / match property / association type are
 * configured in the inline disclosure row beneath. */
export const LINK_SENTINEL = '__link__';

/** Meta-column name: a CSV column mapped to this value is matched to an existing
 * CRM entity by UUID. It is not a CRM property and is shown read-only in the UI. */
export const IMPORT_ENTITY_ID_COLUMN = 'ahoy_import_entity_id';

/** Split-mapping options: one CSV column populates two CRM properties */
export const SPLIT_MAPPING_OPTIONS: {
  value: string;
  label: string;
  entityType: string;
}[] = [
  {
    value: 'ahoy_first_name+ahoy_last_name',
    label: 'Full Name → First + Last',
    entityType: 'ahoy_contact',
  },
];

/** Common lookup properties per CRM entity type for entity association suggestions.
 * Used by both AssociationComponents (to render dropdowns) and MappingStep
 * (to validate auto-populated CRM entity associations from LLM suggestions). */
export const CRM_ENTITY_LOOKUP_PROPERTIES: Record<
  string,
  Array<{ value: string; label: string }>
> = {
  ahoy_user: [
    { value: 'ahoy_email', label: 'Email' },
    { value: 'ahoy_display_name', label: 'Display Name' },
  ],
  ahoy_contact: [
    { value: 'ahoy_email', label: 'Email' },
    { value: 'ahoy_name', label: 'Name' },
  ],
  ahoy_company: [
    { value: 'ahoy_name', label: 'Name' },
    { value: 'ahoy_domain', label: 'Domain' },
  ],
  ahoy_deal: [{ value: 'ahoy_name', label: 'Name' }],
};

/** Field types whose values can serve as a lookup key when matching an imported
 * row to an existing CRM record (free-text identifiers). Selects, datetimes,
 * booleans and associations are excluded — they don't uniquely identify a row. */
export const LOOKUP_FIELD_TYPES = new Set([
  'text',
  'email',
  'url',
  'phone',
  'number',
]);

/** Derive the "match by" lookup-property options for a target entity type from
 * its live property schema. Used for custom (`org_*`) entity types, which have
 * no entry in {@link CRM_ENTITY_LOOKUP_PROPERTIES}. Unique properties sort first
 * so the most reliable match key is the default. */
export function deriveLookupProperties(
  propertyTypes: PropertyTypeSchema[]
): Array<{ value: string; label: string }> {
  return propertyTypes
    .filter(
      p =>
        p.value_read_access !== 'internal' &&
        LOOKUP_FIELD_TYPES.has(p.field_type_name)
    )
    .slice()
    .sort((a, b) => Number(b.is_unique_value) - Number(a.is_unique_value))
    .map(p => ({ value: p.property_type_name, label: p.label }));
}

/** Stable empty sentinels for optional array/object props -- avoids creating new
 * references on every render when the optional suggestion data is absent. */
export const EMPTY_ARRAY: string[] = [];
export const EMPTY_NEW_PROPERTIES: NewPropertySuggestion[] = [];
export const EMPTY_OBJECT: Record<string, string> = {};
export const EMPTY_PROPERTY_TYPES: PropertyTypeSchema[] = [];

/** Column header tooltip descriptions */
export const COLUMN_TOOLTIPS = {
  // NOTE: the source-column tooltip and the "map to property"/"record matching"
  // tooltips live in `import-vocabulary.ts`, not here — their wording depends
  // on whether the import has a file at all.
  fieldType:
    'The data type for new properties. Choose the type that best matches your data.',
} as const;

/**
 * No-op handler passed to FileMappingSection when rendered in pane mode (the
 * sidebar manages activation, so the inner expand/collapse hook is unused).
 */
export const NOOP_EXPANDED_CHANGE = (
  _fileId: string | number,
  _isOpen: boolean
): void => {};

// ============================================================================
// Pure helpers
// ============================================================================

/**
 * Composite key for a (propertyName, entityTypeName) pair — two entity types
 * in a multi-type file can independently derive the same property-name slug,
 * so callers that index/identify a new-property card must key on both, not
 * propertyName alone.
 */
export function propertyIndexKey(
  propertyName: string,
  entityTypeName: string
): string {
  return `${propertyName}::${entityTypeName}`;
}

/** Fallback label when schema isn't loaded yet: "ahoy_first_name" or "org_threat_level" -> "First Name" / "Threat Level" */
export function humanisePropertyName(name: string): string {
  const stripped = name.replace(/^(?:ahoy|org)_/, '').replace(/_/g, ' ');
  return stripped
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Builds an indexed lookup map from `userNewProperties` and `aiNewProperties`,
 * keyed by `propertyIndexKey(property_name, entity_type_name)`.
 *
 * Merge semantics (user-first with empty-label fallthrough): for each property
 * key, the user-created entry wins when its `label.trim()` is non-empty;
 * otherwise the AI entry is used (or the key is absent if no AI entry exists at
 * that key). User-only entries (no corresponding AI entry) with a non-empty
 * label are added under their key.
 *
 * This is the single shared source of truth that both `EntityPreviewCard.tsx`
 * and `MultiTypeMapping.tsx` must use for new-property label resolution so that
 * the preview card, the mapping table, and the New Properties panel can never
 * again disagree about which label wins for a given property.
 */
export function buildNewPropertyIndex(
  userNewProperties: readonly NewPropertySuggestion[],
  aiNewProperties: readonly NewPropertySuggestion[]
): Map<string, NewPropertySuggestion> {
  const index = new Map<string, NewPropertySuggestion>();
  for (const np of aiNewProperties) {
    index.set(propertyIndexKey(np.property_name, np.entity_type_name), np);
  }
  for (const np of userNewProperties) {
    if (np.label.trim()) {
      index.set(propertyIndexKey(np.property_name, np.entity_type_name), np);
    }
    // Empty-label user entries leave the AI entry (if any) in place.
  }
  return index;
}

/**
 * Resolves the display label for a new (not-yet-in-schema) property from the
 * user-created and AI-suggested new-property lists.
 *
 * A user-created or AI-suggested new property (`org_<slug>`) is labelled with
 * the label the user typed / the AI proposed, because it does not exist in the
 * org schema yet -- there is no schema label to fall back on for it. Matching
 * is scoped by (property_name, entity_type_name) so that two entity types
 * which derive the same slug in one multi-type import file resolve
 * independently. User labels take priority over AI labels. Returns `undefined`
 * when there is no usable label so the caller can fall through to the schema
 * label / humanised slug.
 *
 * Delegates to {@link buildNewPropertyIndex} for a single indexed lookup.
 */
export function resolveNewPropertyLabel(
  propertyName: string,
  entityTypeName: string,
  userNewProperties: readonly NewPropertySuggestion[],
  aiNewProperties: readonly NewPropertySuggestion[]
): string | undefined {
  const index = buildNewPropertyIndex(userNewProperties, aiNewProperties);
  const entry = index.get(propertyIndexKey(propertyName, entityTypeName));
  return entry && entry.label.trim() ? entry.label : undefined;
}

// ============================================================================
// Data fetching
// ============================================================================

/** Fetches property types for a given entity type */
export async function fetchPropertyTypes(
  orgId: number,
  entityTypeName: string
): Promise<PropertyTypeSchema[]> {
  const url = `/api/orgs/${orgId}/crm-events/schemas/property-types/?entity_type_name=${encodeURIComponent(entityTypeName)}`;

  const response = await fetchWithDefaults(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch property types: ${response.statusText}`);
  }

  return response.json();
}
